import React from 'react';
import { motion } from 'motion/react';

export default function SnaggleToothedBeastCover() {
  return (
    <div className="absolute inset-0 w-full h-full overflow-hidden select-none bg-emerald-950 font-sans shadow-inner">
      {/* Background Gradient: Warm Golden Sunset over the Fen */}
      <div className="absolute inset-0 w-full h-full bg-gradient-to-b from-[#e3cca3] via-[#fae6ad] to-[#be8e45]">
        
        {/* Soft glowing sun in the horizon center */}
        <div className="absolute left-[35%] bottom-[25%] w-[45%] h-[45%] rounded-full bg-[#fef08a] opacity-85 blur-[25px]" />
        <div className="absolute left-[45%] bottom-[32%] w-[25%] h-[25%] rounded-full bg-white opacity-95 blur-[8px]" />
        
        {/* Sky glow */}
        <div className="absolute top-[15%] left-[20%] w-[60%] h-[45%] rounded-full bg-orange-400/20 blur-[50px]" />

        {/* Water / Swamp surface at the bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-[22%] bg-gradient-to-t from-[#2c2214] to-[#7d6139]/60">
          {/* Subtle water ripples */}
          {[...Array(4)].map((_, i) => (
            <motion.div
              key={`ripple-${i}`}
              className="absolute h-[1.5px] bg-yellow-100/50 rounded-full"
              style={{
                left: `${15 + i * 15}%`,
                width: `${30 + i * 10}%`,
                bottom: `${10 + i * 20}%`,
              }}
              animate={{
                opacity: [0.3, 0.7, 0.3],
                scaleX: [1, 1.15, 1],
              }}
              transition={{
                duration: 3 + i,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          ))}
        </div>

        {/* Backdrop SVG for deep perspective reeds */}
        <svg viewBox="0 0 100 133" className="absolute inset-0 w-full h-full pointer-events-none fill-none z-5 opacity-40">
          {/* Distant background reeds */}
          <path d="M 5 120 Q 15 80 8 40" stroke="#322212" strokeWidth="1" />
          <path d="M 12 120 Q 8 75 16 35" stroke="#322212" strokeWidth="0.8" />
          <path d="M 88 120 Q 82 85 91 42" stroke="#322212" strokeWidth="0.9" />
          <path d="M 94 120 Q 96 70 89 30" stroke="#322212" strokeWidth="1" />
        </svg>

        {/* MAIN ILLUSTRATION LAYER */}
        <div className="absolute inset-x-0 bottom-[12%] h-[68%] z-10 flex justify-center items-end">
          <div className="relative w-[85%] h-full">
            
            <svg viewBox="0 0 120 150" className="absolute inset-0 w-full h-full overflow-visible">
              <defs>
                {/* Radial Gradient for Beast Body */}
                <radialGradient id="beastGrad" cx="50%" cy="50%" r="50%" fx="35%" fy="35%">
                  <stop offset="0%" stopColor="#ac8bc2" />
                  <stop offset="70%" stopColor="#815a99" />
                  <stop offset="100%" stopColor="#5d3973" />
                </radialGradient>
                {/* Linear Gradient for Snaggle-Tooth */}
                <linearGradient id="toothGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#ffffff" />
                  <stop offset="100%" stopColor="#e2e8f0" />
                </linearGradient>
              </defs>

              {/* === THE SNAGGLE-TOOTHED BEAST === */}
              {/* Beast Ears */}
              {/* Left Ear */}
              <path d="M 32 75 C 22 75 24 62 30 55 C 34 50 37 54 38 64 Z" fill="#5d3973" />
              <path d="M 31 73 C 25 73 26 65 30 60 C 33 56 35 59 36 65 Z" fill="#eba480" opacity="0.8" />
              
              {/* Right Ear */}
              <path d="M 88 75 C 98 75 96 62 90 55 C 86 50 83 54 82 64 Z" fill="#5d3973" />
              <path d="M 89 73 C 95 73 94 65 90 60 C 87 56 85 59 84 65 Z" fill="#eba480" opacity="0.8" />

              {/* Beast Body / Shape (Fat & fluffy pear shape) */}
              <path 
                d="M 60 70 
                   C 42 70, 30 82, 28 100 
                   C 26 118, 22 145, 30 152 
                   C 38 160, 82 160, 90 152 
                   C 98 145, 94 118, 92 100 
                   C 90 82, 78 70, 60 70 Z" 
                fill="url(#beastGrad)" 
              />

              {/* Orange Spots on Beast's Body */}
              <ellipse cx="36" cy="115" rx="4" ry="6" fill="#f09667" opacity="0.85" transform="rotate(-15 36 115)" />
              <ellipse cx="33" cy="130" rx="3" ry="5" fill="#f09667" opacity="0.85" transform="rotate(10 33 130)" />
              <ellipse cx="40" cy="144" rx="4.5" ry="3.5" fill="#f09667" opacity="0.85" />
              
              <ellipse cx="84" cy="118" rx="4.5" ry="5.5" fill="#f09667" opacity="0.85" transform="rotate(20 84 118)" />
              <ellipse cx="88" cy="132" rx="3.5" ry="5" fill="#f09667" opacity="0.85" transform="rotate(-10 88 132)" />
              <ellipse cx="80" cy="145" rx="4.5" ry="3.5" fill="#f09667" opacity="0.85" />

              {/* Fluffy Cheeks Orange Blush */}
              <ellipse cx="42" cy="100" rx="6" ry="4" fill="#f59e0b" opacity="0.4" />
              <ellipse cx="78" cy="100" rx="6" ry="4" fill="#f59e0b" opacity="0.4" />

              {/* Big Friendly Eyes (Yellow!) */}
              {/* Left Eye Socket */}
              <circle cx="48" cy="92" r="7.5" fill="#fef08a" stroke="#2e1041" strokeWidth="0.5" />
              {/* Animated Left Pupil */}
              <motion.circle 
                cx="49" 
                cy="92" 
                r="4.5" 
                fill="#1e1b4b"
                animate={{
                  scaleY: [1, 1, 0.1, 1, 1],
                  y: [0, 0.2, 0, -0.2, 0]
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  repeatType: "loop",
                  times: [0, 0.85, 0.9, 0.95, 1],
                  ease: "easeInOut"
                }}
              />
              {/* Left Pupil Highlight */}
              <circle cx="47.5" cy="90" r="1.5" fill="#ffffff" />

              {/* Right Eye Socket */}
              <circle cx="72" cy="92" r="7.5" fill="#fef08a" stroke="#2e1041" strokeWidth="0.5" />
              {/* Animated Right Pupil */}
              <motion.circle 
                cx="71" 
                cy="92" 
                r="4.5" 
                fill="#1e1b4b"
                animate={{
                  scaleY: [1, 1, 0.1, 1, 1],
                  y: [0, 0.2, 0, -0.2, 0]
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  repeatType: "loop",
                  times: [0, 0.85, 0.9, 0.95, 1],
                  ease: "easeInOut"
                }}
              />
              {/* Right Pupil Highlight */}
              <circle cx="69.5" cy="90" r="1.5" fill="#ffffff" />

              {/* Dark purple cute nose */}
              <path d="M 56 94 Q 60 91 64 94 Q 65 96 60 98 Q 55 96 56 94 Z" fill="#31193d" />

              {/* Big happy smile */}
              <path d="M 43 103 Q 60 114 77 103" stroke="#2e1041" strokeWidth="2" strokeLinecap="round" fill="none" />
              
              {/* TWO CUTE SNAGGLE TEETH (pointing up from bottom lip) */}
              {/* Left Snaggle-Tooth */}
              <path d="M 51 106 L 53 100 L 55 105 Z" fill="url(#toothGrad)" stroke="#4a154b" strokeWidth="0.5" />
              {/* Right Snaggle-Tooth */}
              <path d="M 65 105 L 67 100 L 69 106 Z" fill="url(#toothGrad)" stroke="#4a154b" strokeWidth="0.5" />

              {/* Fluffy Arms/Claws resting on belly */}
              {/* Left Hand */}
              <path d="M 28 122 C 34 122 36 126 40 125 C 44 124 43 130 38 131 C 32 132 27 127 28 122 Z" fill="#714e8a" />
              {/* Right Hand */}
              <path d="M 92 122 C 86 122 84 126 80 125 C 76 124 77 130 82 131 C 88 132 93 127 92 122 Z" fill="#714e8a" />


              {/* === THE LITTLE MOUSE (Sitting on head) === */}
              {/* Mouse is positioned at X=60, Y=50 */}
              <g transform="translate(60, 52)">
                {/* Mouse Tail */}
                <path d="M -5 18 Q -14 18 -12 10" stroke="#eba4a7" strokeWidth="1" fill="none" />

                {/* Mouse Body (wearing a red backpack) */}
                {/* Backpack (Red) */}
                <path d="M -5 7 C -8 7 -9 11 -8 14 C -6 16 -3 15 -3 12 Z" fill="#b91c1c" />
                <path d="M -5 8 L -2 9" stroke="#fed7aa" strokeWidth="0.5" /> {/* straps */}
                
                {/* Body (Grey) */}
                <ellipse cx="0" cy="11" rx="4.5" ry="6" fill="#8e8e93" />
                {/* White belly patch */}
                <ellipse cx="1.5" cy="11" rx="2" ry="4.5" fill="#f3f4f6" />

                {/* Mouse Head */}
                <circle cx="1" cy="5" r="4.5" fill="#8e8e93" />
                
                {/* Mouse Ears */}
                {/* Left Ear */}
                <circle cx="-2.5" cy="1.5" r="2.5" fill="#8e8e93" />
                <circle cx="-2.5" cy="1.5" r="1.5" fill="#eba4a7" />
                {/* Right Ear */}
                <circle cx="4.5" cy="1.5" r="2.5" fill="#8e8e93" />
                <circle cx="4.5" cy="1.5" r="1.5" fill="#eba4a7" />

                {/* Mouse Eyes & Nose */}
                <circle cx="2" cy="4.5" r="0.6" fill="#000000" />
                <circle cx="4" cy="5" r="0.6" fill="#000000" />
                <circle cx="5" cy="5.8" r="0.6" fill="#db2777" /> {/* pink nose */}

                {/* Mouse Straw Hat */}
                {/* Hat Crown */}
                <path d="M -2.5 -1.5 C -2.5 -4.5 3.5 -4.5 3.5 -1.5 Z" fill="#d97706" />
                {/* Red ribbon band */}
                <rect x="-2.5" y="-1.5" width="6" height="0.8" fill="#ef4444" />
                {/* Hat Brim */}
                <ellipse cx="0.5" cy="-0.5" rx="5" ry="1" fill="#f59e0b" />

                {/* Mouse Arms holding silver tongs */}
                {/* Left Arm holding beast head */}
                <path d="M -3 10 C -5 9 -4 13 -3 12" stroke="#8e8e93" strokeWidth="1" fill="none" />
                {/* Right Arm extended holding silver tongs */}
                <path d="M 3 9 C 7 7 8 10 9 10" stroke="#8e8e93" strokeWidth="1.2" strokeLinecap="round" fill="none" />
                
                {/* Silver Tongs */}
                <g transform="translate(8, 7) rotate(20)">
                  <path d="M 0 0 L 0 -5 C -0.5 -7 -1.5 -5 -1 -3" stroke="#94a3b8" strokeWidth="0.6" fill="none" />
                  <path d="M 0 0 L 1.5 -5 C 2 -7 3 -5 2.5 -3" stroke="#94a3b8" strokeWidth="0.6" fill="none" />
                  <circle cx="0.4" cy="0" r="0.7" fill="#64748b" /> {/* hinge */}
                </g>
                
                {/* Little legs */}
                <path d="M -2 16 Q -2 19 -1 18" stroke="#8e8e93" strokeWidth="1.2" fill="none" />
                <path d="M 2 16 Q 3 19 4 18" stroke="#8e8e93" strokeWidth="1.2" fill="none" />
              </g>

            </svg>
            
          </div>
        </div>

        {/* SWAYING FOREGROUND REEDS / CATTAILS (framed exactly like the illustration) */}
        <div className="absolute inset-0 pointer-events-none z-15">
          <svg viewBox="0 0 100 133" className="absolute inset-0 w-full h-full fill-none overflow-visible">
            
            {/* Left Reeds Group */}
            <motion.g
              animate={{ rotate: [-2, 1.5, -2] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              style={{ transformOrigin: "10% 120%" }}
            >
              {/* Reed 1 (Cattail) */}
              <path d="M 8 133 C 8 100, 15 70, 11 40" stroke="#4a3b2c" strokeWidth="1.5" />
              {/* Dark Brown Cattail Head */}
              <rect x="9.5" y="32" width="3" height="12" rx="1.5" fill="#422e1b" />
              {/* Spike tip */}
              <path d="M 11 32 L 11 28" stroke="#422e1b" strokeWidth="0.8" />

              {/* Reed 2 (Cattail) */}
              <path d="M 16 133 C 14 95, 24 65, 20 30" stroke="#4a3b2c" strokeWidth="1.2" />
              <rect x="18.5" y="22" width="2.6" height="11" rx="1.3" fill="#4a321a" />
              <path d="M 19.8 22 L 19.8 18" stroke="#4a321a" strokeWidth="0.7" />

              {/* Blade of leaf */}
              <path d="M 8 133 C 12 110, 22 95, 24 75 Q 26 55 12 45" stroke="#5a4f3b" strokeWidth="1.2" />
            </motion.g>

            {/* Right Reeds Group */}
            <motion.g
              animate={{ rotate: [1.5, -2, 1.5] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              style={{ transformOrigin: "90% 120%" }}
            >
              {/* Reed 3 (Cattail) */}
              <path d="M 91 133 C 91 100, 84 70, 88 38" stroke="#4a3b2c" strokeWidth="1.5" />
              <rect x="86.5" y="28" width="3" height="12" rx="1.5" fill="#422e1b" />
              <path d="M 88 28 L 88 24" stroke="#422e1b" strokeWidth="0.8" />

              {/* Reed 4 (Cattail) */}
              <path d="M 83 133 C 84 92, 75 68, 77 42" stroke="#4a3b2c" strokeWidth="1.2" />
              <rect x="75.6" y="34" width="2.6" height="10" rx="1.3" fill="#4a321a" />
              <path d="M 76.9 34 L 76.9 30" stroke="#4a321a" strokeWidth="0.7" />

              {/* Blade of leaf */}
              <path d="M 91 133 C 86 110, 74 95, 72 70 Q 70 50 86 42" stroke="#5a4f3b" strokeWidth="1.2" />
            </motion.g>
            
          </svg>
        </div>

        {/* BUZZING DRAGONFLIES */}
        <div className="absolute inset-0 pointer-events-none z-20">
          {/* Dragonfly 1 */}
          <motion.div
            className="absolute w-6 h-4"
            animate={{
              x: [20, 85, 30, 20],
              y: [45, 35, 55, 45],
            }}
            transition={{
              duration: 12,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            <svg viewBox="0 0 20 15" className="w-full h-full">
              {/* Body */}
              <line x1="2" y1="7.5" x2="16" y2="7.5" stroke="#f3f4f6" strokeWidth="0.8" />
              <circle cx="2" cy="7.5" r="1" fill="#fbbf24" />
              {/* Fluttering Wings */}
              <motion.path 
                d="M 6 7.5 Q 4 1, 6 1 Q 8 1, 6 7.5 M 6 7.5 Q 4 14, 6 14 Q 8 14, 6 7.5" 
                fill="#fef08a" 
                opacity="0.8"
                animate={{ scaleY: [1, -1, 1] }}
                transition={{ duration: 0.1, repeat: Infinity }}
              />
            </svg>
          </motion.div>

          {/* Dragonfly 2 */}
          <motion.div
            className="absolute w-5 h-3"
            animate={{
              x: [80, 15, 70, 80],
              y: [60, 50, 40, 60],
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            <svg viewBox="0 0 20 15" className="w-full h-full">
              {/* Body */}
              <line x1="2" y1="7.5" x2="16" y2="7.5" stroke="#f3f4f6" strokeWidth="0.8" />
              <circle cx="2" cy="7.5" r="0.8" fill="#fbbf24" />
              {/* Fluttering Wings */}
              <motion.path 
                d="M 6 7.5 Q 4 2, 6 2 Q 8 2, 6 7.5 M 6 7.5 Q 4 13, 6 13 Q 8 13, 6 7.5" 
                fill="#fef08a" 
                opacity="0.8"
                animate={{ scaleY: [-1, 1, -1] }}
                transition={{ duration: 0.08, repeat: Infinity }}
              />
            </svg>
          </motion.div>
        </div>

        {/* ORGANIC MULTI-LAYERED MOSSY BORDER FRAME */}
        {/* Layer 1 (Dark green moss/foliage shadow layer) */}
        <svg viewBox="0 0 100 133" className="absolute inset-0 w-full h-full pointer-events-none z-25 overflow-visible fill-[#0e1c12]">
          <path d="M -5 -5 L 105 -5 L 105 16 C 90 12, 80 19, 70 12 C 58 8, 48 17, 38 10 C 28 15, 14 8, -5 16 Z" />
          <path d="M -5 138 L 105 138 L 105 117 C 90 121, 80 114, 70 120 C 58 124, 48 116, 38 122 C 28 118, 14 124, -5 117 Z" />
          <path d="M -5 -5 L -5 138 L 11 138 C 7 120, 13 100, 7 80 C 11 60, 5 40, 9 20 C 5 10, 9 -5, -5 -5 Z" />
          <path d="M 105 -5 L 105 138 L 89 138 C 93 120, 87 100, 93 80 C 89 60, 95 40, 91 20 C 95 10, 91 -5, 105 -5 Z" />
        </svg>

        {/* Layer 2 (Lighter forest green moss layer peaking out slightly) */}
        <svg viewBox="0 0 100 133" className="absolute inset-0 w-full h-full pointer-events-none z-24 overflow-visible fill-[#1a3320]">
          <path d="M -5 -5 L 105 -5 L 105 19 C 90 15, 80 23, 70 16 C 58 11, 48 20, 38 13 C 28 18, 14 11, -5 20 Z" />
          <path d="M -5 138 L 105 138 L 105 114 C 90 118, 80 110, 70 117 C 58 121, 48 112, 38 119 C 28 114, 14 121, -5 114 Z" />
          <path d="M -5 -5 L -5 138 L 14 138 C 10 120, 16 100, 10 80 C 14 60, 8 40, 12 20 C 8 10, 12 -5, -5 -5 Z" />
          <path d="M 105 -5 L 105 138 L 86 138 C 90 120, 84 100, 90 80 C 86 60, 92 40, 88 20 C 92 10, 88 -5, 105 -5 Z" />
        </svg>

        {/* TYPOGRAPHY OVERLAYS */}
        {/* Header Title: Hand-drawn friendly styled text */}
        <div className="absolute inset-x-[25px] top-[25px] z-30 flex flex-col items-center justify-start text-center">
          <h1 className="font-sans font-black tracking-tight text-[#faedd5] leading-[1.15] filter drop-shadow-[0_2px_4px_rgba(0,0,0,0.95)]">
            <span className="block text-[17px] uppercase tracking-wide text-[#fed7aa] font-extrabold mb-0.5">
              THE SNAGGLE-TOOTHED BEAST
            </span>
            <span className="block text-[13px] uppercase tracking-widest text-[#fde047] font-bold">
              OF BOGGLE-DOWN FEN
            </span>
          </h1>
        </div>

        {/* Footer Credit: Author / Illustrator details */}
        <div className="absolute inset-x-0 bottom-[24px] z-30 flex flex-col items-center text-center text-[#faedd5] filter drop-shadow-[0_1.5px_2px_rgba(0,0,0,0.95)] font-sans">
          <p className="text-[12px] font-extrabold tracking-wide text-white">
            By Willow Wise
          </p>
          <p className="text-[8px] font-bold uppercase tracking-[0.2em] text-[#fed7aa] mt-0.5">
            Illustrator and Storyteller
          </p>
        </div>

      </div>
    </div>
  );
}
